import type { BatteryPlug, NetworkType, PhoneMetrics, SimState } from '../types'

/** Sentinel for "no data yet" — agent hasn't pushed a snapshot. */
export const DASH = '—'

const SIM_LABEL: Record<SimState, string> = {
  ready: 'Готова',
  absent: 'Нет SIM',
  locked: 'Заблокирована',
  network_locked: 'Сетевая блокировка',
  unknown: 'Неизвестно',
}

const NETWORK_LABEL: Record<NetworkType, string> = {
  lte: 'LTE',
  '5g': '5G',
  nr: '5G',
  hspa: '3G+',
  umts: '3G',
  edge: 'EDGE',
  gprs: 'GPRS',
  wifi: 'Wi-Fi',
  none: 'нет',
  unknown: '?',
}

export function formatSimState(state: SimState | null): string {
  if (state === null) return DASH
  return SIM_LABEL[state] ?? DASH
}

export function formatNetwork(type: NetworkType | null): string {
  if (type === null) return DASH
  return NETWORK_LABEL[type] ?? DASH
}

/**
 * Compose a sortable signal label: "LTE ▮▮▮▯" with bars 0-4.
 * Returns "—" when both inputs absent (cold-boot phone).
 */
export function formatSignal(metrics: Pick<PhoneMetrics, 'networkType' | 'signalBars'>): string {
  const net = metrics.networkType
  const bars = metrics.signalBars
  if (net === null && bars === null) return DASH
  const netLabel = net !== null ? (NETWORK_LABEL[net] ?? DASH) : ''
  if (bars === null) return netLabel || DASH
  const filled = '▮'.repeat(Math.max(0, Math.min(4, bars)))
  const empty = '▯'.repeat(Math.max(0, 4 - Math.max(0, Math.min(4, bars))))
  return netLabel ? `${netLabel} ${filled}${empty}` : `${filled}${empty}`
}

/**
 * Battery as "75% ⚡" (charging) / "23%" / "—". Charging icon is the
 * lightning emoji; UI consumers may swap for an SVG by checking the
 * boolean directly instead of this helper.
 */
export function formatBattery(pct: number | null, charging: boolean | null): string {
  if (pct === null) return DASH
  return charging === true ? `${pct}% ⚡` : `${pct}%`
}

const PLUGGED_LABEL: Record<BatteryPlug, string> = {
  ac: 'AC',
  usb: 'USB',
  wireless: 'беспроводная',
  none: '',
}

export function formatPlugged(plugged: BatteryPlug | null): string {
  if (plugged === null || plugged === 'none') return ''
  return PLUGGED_LABEL[plugged] ?? ''
}

/** "12.3 ГБ" / "456 МБ" / "—". */
export function formatStorage(mb: number | null): string {
  if (mb === null) return DASH
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} ГБ`
  return `${mb} МБ`
}

/**
 * "5д 3ч" / "12ч 4м" / "45м" / "12с" / "—". Buckets coarsen as value
 * grows — admins glance at it, sub-precision is noise.
 */
export function formatUptime(seconds: number | null): string {
  if (seconds === null || seconds < 0) return DASH
  if (seconds < 60) return `${seconds}с`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}м`
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return `${h}ч ${m}м`
  }
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  return `${d}д ${h}ч`
}

/**
 * "только что" / "12с назад" / "5м назад" / "3ч назад" / "3д назад" / "—".
 * `now` is injected for testability. Future timestamps (server clock
 * skew) collapse to "только что" rather than negative values.
 */
export function formatRelativeTime(iso: string | null, now: Date = new Date()): string {
  if (iso === null) return DASH
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return DASH
  const diffSec = Math.floor((now.getTime() - t) / 1000)
  if (diffSec < 30) return 'только что'
  if (diffSec < 60) return `${diffSec}с назад`
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}м назад`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}ч назад`
  return `${Math.floor(diffSec / 86400)}д назад`
}

/** Battery threshold for the red "low battery" admin signal. */
export const LOW_BATTERY_THRESHOLD = 15

/** Signal threshold for the yellow "weak signal" admin signal. */
export const WEAK_SIGNAL_BARS = 1
